import React, { useEffect } from "react";
import { Container, Row } from "react-bootstrap";
import { useLoaderData } from "react-router";
import { Link } from "react-router-dom";

import "@css/pages/playoffs.scss";

import { IAPIResponse, ILeague, IPlayoff } from "@src/types/api";

import { alertActions } from "@features/alert/alertSlice";

import { useAppDispatch } from "@hooks/reduxHooks";
import useBreakpoint from "@hooks/useBreakpoint";

import { TEAM_LOGO_URL } from "@lib/globals";
import { isXOrSmaller } from "@lib/utils";

const PlayoffBracket = (): React.JSX.Element => {
    const {
        bracket: { bracket },
        league,
    } = useLoaderData() as {
        bracket: IAPIResponse & {
            bracket: IPlayoff;
        };
        league: ILeague | undefined;
    };
    const dispatch = useAppDispatch();
    const breakpoint = useBreakpoint();

    const buildBracket = (bracketType: "championship" | "consolation") => {
        let bracketOutput = [];
        for (let i = 1; i <= bracket.rounds[bracketType]!; i += 1) {
            bracketOutput.push(
                <div className={`round round-${i}`} key={`round-${i}`}>
                    {bracket.games[bracketType]!.filter(g => g.round === i).map(
                        (g, idx, games) => {
                            let date =
                                g.date !== null
                                    ? new Intl.DateTimeFormat("en-US", {
                                          dateStyle: "long",
                                          timeStyle: "short",
                                      }).format(new Date(g.date))
                                    : "TBD";
                            const isChampionshipGame =
                                bracketType === "championship" &&
                                i === bracket.rounds[bracketType] &&
                                idx === games.length - 1;

                            return (
                                <div
                                    className="match"
                                    id={`${bracketType}-game-${g.playoffGameId}`}
                                    key={`${bracketType}-match-${g.playoffGameId}`}
                                >
                                    {g.teams.visitor.seed ||
                                    g.teams.visitor.team ? (
                                        <div className="team">
                                            <span className="seed">
                                                #
                                                {g.teams.visitor.id
                                                    ? league?.standings?.[
                                                          g.teams.visitor.id
                                                      ].standing
                                                    : g.teams.visitor.seed}
                                            </span>
                                            {g.teams.visitor.logo !== null && (
                                                <span className="teamLogo">
                                                    <img
                                                        src={`${TEAM_LOGO_URL}${g.teams.visitor.logo}`}
                                                        alt={`${g.teams.visitor.team} logo`}
                                                    />
                                                </span>
                                            )}
                                            <span className="teamName">
                                                {g.teams.visitor.team}
                                            </span>
                                            {g.teams.visitor.score !== null && (
                                                <span className="score">
                                                    {g.teams.visitor.score}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="team winnerOf">
                                            Winner of{" "}
                                            {bracketType === "consolation" &&
                                                "Consolation"}{" "}
                                            Game {g.teams.visitor.winnerOf}
                                        </div>
                                    )}

                                    <div className="gameInfo">
                                        <span className="gameId">
                                            {isChampionshipGame
                                                ? "Championship"
                                                : bracketType ===
                                                      "consolation" &&
                                                  "Consolation"}{" "}
                                            Game{" "}
                                            {!isChampionshipGame &&
                                                g.playoffGameId}
                                        </span>
                                        <span className="date">
                                            {g.status !== "Postponed"
                                                ? date
                                                : "TBD"}
                                        </span>
                                        <span className="field">
                                            {g.field &&
                                            ![512, 532].includes(g.field) ? (
                                                <Link
                                                    to={`/fields/${g.field}`}
                                                    target="_blank"
                                                >
                                                    {g.fieldName ? (
                                                        g.diamond ? (
                                                            <React.Fragment>
                                                                {`${g.fieldName}`}
                                                                <br />
                                                                Field:{" "}
                                                                {`${g.diamond}`}
                                                            </React.Fragment>
                                                        ) : (
                                                            g.fieldName
                                                        )
                                                    ) : (
                                                        "Home Team's Field"
                                                    )}
                                                </Link>
                                            ) : (
                                                <React.Fragment>
                                                    {g.fieldName ? (
                                                        g.diamond ? (
                                                            <React.Fragment>
                                                                {`${g.fieldName}`}
                                                                <br />
                                                                Field:{" "}
                                                                {`${g.diamond}`}
                                                            </React.Fragment>
                                                        ) : (
                                                            g.fieldName
                                                        )
                                                    ) : (
                                                        "Home Team's Field"
                                                    )}
                                                </React.Fragment>
                                            )}
                                        </span>
                                    </div>

                                    {g.teams.home.seed || g.teams.home.team ? (
                                        <div className="team">
                                            <span className="seed">
                                                #
                                                {g.teams.home.id
                                                    ? league?.standings?.[
                                                          g.teams.home.id
                                                      ].standing
                                                    : g.teams.home.seed}
                                            </span>
                                            {g.teams.home.logo !== null && (
                                                <span className="teamLogo">
                                                    <img
                                                        src={`${TEAM_LOGO_URL}${g.teams.home.logo}`}
                                                        alt={`${g.teams.home.team} logo`}
                                                    />
                                                </span>
                                            )}
                                            <span className="teamName">
                                                {g.teams.home.team}
                                            </span>
                                            {g.teams.home.score !== null && (
                                                <span className="score">
                                                    {g.teams.home.score}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="team winnerOf">
                                            Winner of{" "}
                                            {bracketType === "consolation" &&
                                                "Consolation"}{" "}
                                            Game {g.teams.home.winnerOf}
                                        </div>
                                    )}
                                </div>
                            );
                        },
                    )}
                </div>,
            );
        }

        if (league?.excludedTeams?.length) {
            bracketOutput.push(
                <div className="round excludedTeams" key="excludedTeams">
                    <div className="match">
                        <div className="team">
                            Teams not participating in playoffs
                        </div>
                        <div className="gameInfo">
                            {league.excludedTeams?.map(et => {
                                const team = league.teams?.find(
                                    t => t.id === et,
                                );

                                return (
                                    <div key={et}>
                                        <span className="seed">
                                            {team?.id &&
                                            league?.standings?.[team.id]
                                                .standing
                                                ? `#${league.standings?.[team.id].standing}`
                                                : ""}
                                        </span>
                                        {team?.logo !== null && (
                                            <span className="teamLogo">
                                                <img
                                                    src={`${TEAM_LOGO_URL}${team?.logo}`}
                                                    alt={`${team?.name} logo`}
                                                />
                                            </span>
                                        )}
                                        <span className="teamName">
                                            {team?.name}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>,
            );
        }

        return bracketOutput;
    };

    useEffect(() => {
        if (isXOrSmaller("md", breakpoint)) {
            dispatch(
                alertActions.warning({
                    title: "Display Notice",
                    text: "Due to playoff bracket layouts, this page looks best at resolutions of 992px or higher",
                    dismissible: true,
                }),
            );
        } else {
            dispatch(alertActions.clear());
        }
    }, [breakpoint]);

    return (
        <Container data-testid="playoffBracketContent">
            <Row
                className="bracket"
                data-bracketid={bracket.bracketId}
                key={bracket.bracketId}
            >
                {buildBracket("championship")}
                {bracket.games.consolation && (
                    <>
                        <h3>Consolation Games</h3>
                        {buildBracket("consolation")}
                    </>
                )}
            </Row>
        </Container>
    );
};

export default PlayoffBracket;
